import CallErrorMessage, { Severity } from 'app/components/call/CallErrors/CallErrorMessage';
import { tFunctionType, useI18n } from 'app/components/localization';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import React from 'react';
import { useRtcState } from '../../../containers/rtc/RtcStateContext';
import { useRTC } from '../../../containers/rtc/Provider';
import { BookingType, BookingTypeSettings } from '../../../models/rtc/booking/constants';
import { getSignalingErrorMessage } from '../../../models/ui/call/errorMessages';
import { ITimeoutWarning } from '../../../services/rtc/constants';
import MarginGroup from '../../layout/MarginGroup';
import CountdownMessage from './CountdownMessage';

interface IDeps {
  reconnect?: () => void;
  t: tFunctionType;
}

const SignalingErrorMessage = ({ signalingError, t, reconnect }: { signalingError: Error } & IDeps) =>
  <CallErrorMessage
    severity={Severity.Info}
    buttonAction={reconnect}
    buttonLabel={reconnect && t('rtc.errors.timeout.reconnect-now')}
  >
    {getSignalingErrorMessage(t, signalingError)}
  </CallErrorMessage>;

const SignalingTimeoutMessage = ({
  signalingError,
  signalingTimeout,
  t,
  reconnect,
}: {
  signalingError?: Error,
  signalingTimeout: ITimeoutWarning,
} & IDeps) =>
  <CountdownMessage
    severity={Severity.Info}
    timeoutWarning={signalingTimeout}
    buttonAction={reconnect}
    buttonLabel={reconnect && t('rtc.errors.timeout.reconnect-now')}
  >
    {(count) => <MarginGroup>
      {signalingError
        ? getSignalingErrorMessage(t, signalingError)
        : t('rtc.errors.timeout.message.server')}

      {t('rtc.errors.timeout.message.trying', { count })}
    </MarginGroup>}
  </CountdownMessage>;

/**
 * Shows an error message and/or countdown to reconnect message. It
 * depens on whether both `signalingError` and `signalingTimeout`
 * is present or only one of them. In all cases "Reconnect now"
 * button is present.
 *
 * - only `signalingError`: message for that error
 * - only `signalingTimeout`: timeout message + countdown timer
 * - both: message for the error + countdown timer
 * - none: nothing ;-)
 */
const SignalingErrorMessageInjectDependencies = () => {
  const rtc = useRTC();
  const bookingState = useBookingState();
  const { signalingError, signalingTimeout } = useRtcState();
  const { t } = useI18n();

  if (!bookingState || !rtc) return null;

  if (signalingTimeout) {
    return (
      <SignalingTimeoutMessage
        t={t}
        signalingError={signalingError}
        signalingTimeout={signalingTimeout}
        reconnect={disableReconnect(bookingState.bookingType) ? undefined : rtc.reconnect}
      />
    );
  }

  if (signalingError) {
    return (
      <SignalingErrorMessage
        t={t}
        signalingError={signalingError}
        reconnect={disableReconnect(bookingState.bookingType) ? undefined : rtc.reconnect}
      />
    );
  }

  return null;
};

const disableReconnect = (bookingType: BookingType) =>
  BookingTypeSettings[bookingType].controlsConnection;

export default SignalingErrorMessageInjectDependencies;
